import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { formatCurrency } from "@/config/company.config";
import { 
  FileText, Clock, DollarSign, Plus, Search, AlertCircle, 
  CheckCircle, User, Calendar
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface Mandate {
  id: string;
  clientId: string;
  clientName: string;
  title: string;
  type: string;
  status: 'active' | 'completed' | 'pending' | 'on_hold';
  priority: 'high' | 'medium' | 'low';
  startDate: Date;
  dueDate?: Date;
  hourlyRate: number;
  estimatedHours: number;
  loggedHours: number;
  totalFee: number;
  progress: number;
  description?: string;
}

const mockMandates: Mandate[] = [
  {
    id: "mandate-1",
    clientId: "client-1",
    clientName: "Jean-Pierre Martin",
    title: "Due Diligence Review",
    type: "Legal Review",
    status: "active",
    priority: "high",
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
    hourlyRate: 250,
    estimatedHours: 40,
    loggedHours: 28,
    totalFee: 10000,
    progress: 70,
    description: "Complete legal due diligence for acquisition",
  },
  {
    id: "mandate-2",
    clientId: "client-2",
    clientName: "Sophie Tremblay",
    title: "Contract Drafting",
    type: "Contract Services",
    status: "active",
    priority: "medium",
    startDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    hourlyRate: 200,
    estimatedHours: 20,
    loggedHours: 8,
    totalFee: 4000,
    progress: 40,
  },
  {
    id: "mandate-3",
    clientId: "client-3",
    clientName: "Marc Dubois",
    title: "Tax Planning Strategy",
    type: "Tax Advisory",
    status: "pending",
    priority: "low",
    startDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    hourlyRate: 175,
    estimatedHours: 15,
    loggedHours: 0,
    totalFee: 2625,
    progress: 0,
  },
];

const translations = {
  en: {
    title: "Mandates",
    subtitle: "Manage your active and completed mandates",
    newMandate: "New Mandate",
    searchMandates: "Search mandates...",
    allStatus: "All Status",
    active: "Active",
    completed: "Completed",
    pending: "Pending",
    onHold: "On Hold",
    noMandates: "No mandates found",
    createFirst: "Create your first mandate to get started",
    client: "Client",
    type: "Type",
    dueDate: "Due Date",
    progress: "Progress",
    rate: "Rate",
    hours: "Hours",
    logged: "Logged",
    fee: "Fee",
    high: "High",
    medium: "Medium",
    low: "Low",
    overdue: "Overdue",
    loading: "Loading mandates...",
  },
  fr: {
    title: "Mandats",
    subtitle: "Gérez vos mandats actifs et complétés",
    newMandate: "Nouveau Mandat",
    searchMandates: "Rechercher mandats...",
    allStatus: "Tous les Statuts",
    active: "Actif",
    completed: "Complété",
    pending: "En Attente",
    onHold: "En Pause",
    noMandates: "Aucun mandat trouvé",
    createFirst: "Créez votre premier mandat pour commencer",
    client: "Client",
    type: "Type",
    dueDate: "Échéance",
    progress: "Progression",
    rate: "Taux",
    hours: "Heures",
    logged: "Notées",
    fee: "Honoraires",
    high: "Haute",
    medium: "Moyenne",
    low: "Basse",
    overdue: "En Retard",
    loading: "Chargement des mandats...",
  },
};

export default function PartnerMandates() {
  const { partnerType } = useParams<{ partnerType: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [mandates, setMandates] = useState<Mandate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  useEffect(() => {
    if (user) {
      loadMandates();
    }
  }, [user]);

  const loadMandates = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const mandatesQuery = query(
        collection(db, 'partnerMandates'),
        where('partnerId', '==', user.email),
        orderBy('startDate', 'desc')
      );
      const mandatesSnapshot = await getDocs(mandatesQuery);
      const mandatesList = mandatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        startDate: doc.data().startDate?.toDate(),
        dueDate: doc.data().dueDate?.toDate(),
      })) as Mandate[];
      setMandates(mandatesList.length > 0 ? mandatesList : mockMandates);
    } catch (error) {
      console.error('Error fetching mandates:', error);
      setMandates(mockMandates);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date: any) => {
    if (!date) return '-';
    const d = date instanceof Date ? date : new Date(date);
    return format(d, 'MMM d, yyyy', { locale: language === 'fr' ? fr : undefined });
  };

  const isOverdue = (dueDate?: Date) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date() && mandates.some(m => m.status === 'active');
  };

  const getStatusBadgeVariant = (status: string): "default" | "secondary" | "outline" | "destructive" => {
    switch (status) {
      case 'active': return 'default';
      case 'completed': return 'secondary';
      case 'pending': return 'outline';
      case 'on_hold': return 'destructive';
      default: return 'outline';
    }
  };

  const getPriorityBadgeVariant = (priority: string): "default" | "secondary" | "outline" => {
    switch (priority) {
      case 'high': return 'default';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const filteredMandates = mandates.filter(mandate => {
    const matchesSearch = mandate.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         mandate.clientName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || mandate.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        </div>
        <Button onClick={() => navigate(`/internal-partners/${partnerType}/new-mandate`)}>
          <Plus className="mr-2 h-4 w-4" />
          {t.newMandate}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t.searchMandates}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-[180px]">
            <SelectValue placeholder={t.allStatus} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.allStatus}</SelectItem>
            <SelectItem value="active">{t.active}</SelectItem>
            <SelectItem value="completed">{t.completed}</SelectItem>
            <SelectItem value="pending">{t.pending}</SelectItem>
            <SelectItem value="on_hold">{t.onHold}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Mandates List */}
      {filteredMandates.length > 0 ? (
        <div className="space-y-4">
          {filteredMandates.map((mandate) => (
            <Card key={mandate.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge variant={getStatusBadgeVariant(mandate.status)}>
                        {t[mandate.status as keyof typeof t] || mandate.status}
                      </Badge>
                      <Badge variant={getPriorityBadgeVariant(mandate.priority)}>
                        {t[mandate.priority as keyof typeof t]}
                      </Badge>
                      {isOverdue(mandate.dueDate) && (
                        <Badge variant="destructive">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {t.overdue}
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-lg">{mandate.title}</CardTitle>
                    {mandate.description && (
                      <CardDescription className="mt-1">{mandate.description}</CardDescription>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">{t.client}</p>
                      <p className="text-sm font-medium">{mandate.clientName}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">{t.type}</p>
                      <p className="text-sm font-medium">{mandate.type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">{t.dueDate}</p>
                      <p className="text-sm font-medium">{formatDate(mandate.dueDate)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">{t.fee}</p>
                      <p className="text-sm font-medium">{formatCurrency(mandate.totalFee)}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t.progress}</span>
                    <span className="font-medium">{mandate.progress}%</span>
                  </div>
                  <Progress value={mandate.progress} className="h-2" />
                </div>

                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>{t.rate}: {formatCurrency(mandate.hourlyRate)}/hr</span>
                  <span>•</span>
                  <span>{t.hours}: {mandate.loggedHours}/{mandate.estimatedHours}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">{t.noMandates}</h3>
            <p className="text-muted-foreground text-center mt-2 mb-4">{t.createFirst}</p>
            <Button onClick={() => navigate(`/internal-partners/${partnerType}/new-mandate`)}>
              <Plus className="mr-2 h-4 w-4" />
              {t.newMandate}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
